import { Injectable } from '@angular/core';
import { VariableEditorPermissionLevel } from '@selfai-platform/pipeline-common';
import { PermissionService, PipelinePermission } from '@selfai-platform/shared';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { KeCurrentUserDomainService } from '../../current-user/services';
import { WorkflowDomainService } from '../../wokflow-list/services';

@Injectable()
export class LocalVariablesPermissionService {
  constructor(
    private readonly permissionService: PermissionService,
    private readonly keCurrentUserDomainService: KeCurrentUserDomainService,
    private readonly workflowDomainService: WorkflowDomainService,
  ) {
    this.keCurrentUserDomainService.loadProfileIfEmpty();
  }

  public getUserPermissions(workflowId: string): Observable<VariableEditorPermissionLevel[]> {
    this.workflowDomainService.loadWorkflowIfEmpty(workflowId);

    return combineLatest({
      userId: this.keCurrentUserDomainService.getCurrentUserId(),
      workflow: this.workflowDomainService.getWorkflow(workflowId).pipe(filter(Boolean)),
    }).pipe(
      map(({ userId, workflow }) => {
        const isOwner = userId === workflow.workflowInfo.ownerId;
        const isPublic = workflow.thirdPartyData?.gui?.isPublic || false;

        return [this.canReadVariables(isOwner, isPublic), this.canUpdateVariables(isOwner, isPublic)];
      }),
      map(([canRead, canUpdate]) => {
        if (canRead && !canUpdate) {
          return [VariableEditorPermissionLevel.READ];
        } else if (canRead && canUpdate) {
          return [
            VariableEditorPermissionLevel.READ,
            VariableEditorPermissionLevel.UPDATE,
            VariableEditorPermissionLevel.DELETE,
            VariableEditorPermissionLevel.CREATE,
          ];
        }

        return [];
      }),
    );
  }

  private canReadVariables(isOwner: boolean, isPublic: boolean): boolean {
    if (this.hasPermission(PipelinePermission.WorkflowGetAny)) {
      return true;
    }

    if (this.hasPermission(PipelinePermission.WorkflowGetOwn)) {
      return isOwner || isPublic;
    }

    return false;
  }

  private canUpdateVariables(isOwner: boolean, isPublic: boolean): boolean {
    if (this.hasPermission(PipelinePermission.WorkflowUpdateAny)) {
      return true;
    }

    if (this.hasPermission(PipelinePermission.WorkflowUpdateOwn)) {
      return isOwner || isPublic;
    }

    return false;
  }

  private hasPermission(permission: PipelinePermission): boolean {
    return this.permissionService.getPipelinePermissions().includes(permission);
  }
}
